import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { Toaster } from "sonner";

const euro = (n, c = "EUR") => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: c.toUpperCase() }).format(Number(n));
};

const POLL_MS = 2000;
const MAX_ATTEMPTS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", loading: true });
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);
  const { theme } = useTheme();

  useEffect(() => {
    if (!sessionId) {
      setStatus({ payment_status: "error", loading: false, error: "Session id mancante" });
      return undefined;
    }
    let cancelled = false;
    let timer;
    attemptsRef.current = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        attemptsRef.current += 1;
        setAttempts(attemptsRef.current);
        if (data.payment_status === "paid" || attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus({ ...data, loading: false });
          return;
        }
        timer = setTimeout(poll, POLL_MS);
      } catch (err) {
        if (!cancelled) {
          console.debug("payment status poll failed:", err?.response?.status || err?.message);
          setStatus({ payment_status: "error", loading: false, error: "Impossibile verificare il pagamento" });
        }
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const pending = status.payment_status === "pending" && status.loading !== false;
  const failed = status.payment_status === "failed" || status.payment_status === "error";
  const creditOnly = paid && (status.paid_via_credit || Number(status.credit_applied_eur || 0) > 0 && Number(status.amount || 0) === 0);
  const creditApplied = Number(status.credit_applied_eur || 0);

  return (
    <div className="min-h-screen bg-[var(--bg-1)] flex items-center justify-center px-6" data-testid="payment-success-page">
      <Toaster position="top-right" theme={theme} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="max-w-md w-full bg-[var(--bg-2)] border border-[var(--border-c)] rounded-2xl overflow-hidden shadow-[0_10px_60px_rgba(45,41,38,0.08)]"
      >
        <div className={`h-1.5 ${paid ? "bg-[#4A8A6E]" : failed ? "bg-[#C6423B]" : "bg-[#E9A94E]"}`} />
        <div className="p-8 md:p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-5"
               style={{ background: paid ? "rgba(74,138,110,0.12)" : failed ? "rgba(198,66,59,0.12)" : "rgba(233,169,78,0.12)" }}>
            {paid ? <CheckCircle2 size={26} className="text-[#3E7A5E]" /> :
             failed ? <XCircle size={26} className="text-[#C6423B]" /> :
             <Clock size={26} className="text-[#B57F1F]" />}
          </div>
          <h1 className="font-serif text-3xl text-[var(--fg-1)] tracking-tight">
            {paid ? "Pagamento riuscito" : failed ? "Pagamento non completato" : "Verifica in corso"}
          </h1>
          <p className="mt-3 text-sm text-[var(--fg-3)] leading-relaxed">
            {paid ? (
              creditOnly ? (
                <>Grazie! Ordine completato usando <strong className="text-[var(--fg-1)]">{euro(creditApplied, status.currency)}</strong> di credito referral. La tua RecenUp Card è ora in fase di preparazione.</>
              ) : (
                <>Grazie! Abbiamo ricevuto il tuo pagamento di <strong className="text-[var(--fg-1)]">{euro(status.amount, status.currency)}</strong>{creditApplied > 0 ? <> più <strong className="text-[var(--fg-1)]">{euro(creditApplied, status.currency)}</strong> di credito referral</> : null}. La tua RecenUp Card è ora in fase di preparazione.</>
              )
            ) : failed ? (
              status.error || "Il pagamento non è stato confermato. Puoi riprovare dal link ricevuto via email o contattare l'assistenza."
            ) : (
              "Stiamo confermando il pagamento con l'istituto bancario. Questo passaggio richiede pochi secondi."
            )}
          </p>
          {pending && (
            <div className="mt-6 text-[10px] uppercase tracking-[0.2em] text-[var(--fg-4)]">
              Tentativo {attempts + 1} di {MAX_ATTEMPTS}
            </div>
          )}
          <div className="mt-8">
            <Link
              to="/dashboard"
              data-testid="payment-return-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-6 py-3 text-sm font-sans font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Vai alla dashboard <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
